import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import useQueryMemo from '@/common/hooks/urlMemo';
import {
	ORDER_STATUS_PHASE,
	ORDER_STATUS_TIMING,
} from '../constants/orderStatus.constants';
import { parseOrderStatusQueryParams } from '../utils/parseOrderStatusQueryParams';
import { redirectStripeQuizFailure } from '../utils/orderStatusRedirect';
import { checkQuizOrderStatus } from '@/api/query/payment';

const useOrderStatusPage = () => {
	const query = useQueryMemo();
	const params = useMemo(() => parseOrderStatusQueryParams(query), [query]);
	const [phase, setPhase] = useState(
		params.isStripeSuccess
			? ORDER_STATUS_PHASE.VERIFYING
			: ORDER_STATUS_PHASE.FAILED,
	);
	const [timeoutTriggered, setTimeoutTriggered] = useState(false);
	const paramsRef = useRef(params);
	paramsRef.current = params;

	const finishedRef = useRef(false);
	const cancelledRef = useRef(false);
	const intervalIdRef = useRef(null);
	const timeoutIdRef = useRef(null);

	const stopPolling = () => {
		cancelledRef.current = true;
		clearInterval(intervalIdRef.current);
		clearTimeout(timeoutIdRef.current);
	};

	const { mutateAsync: verifyPayment } = useMutation({
		mutationFn: ({ email, paymentIntent }) =>
			checkQuizOrderStatus({ email, paymentIntent }).then(
				({ data }) => Boolean(data?.isPaid),
			),
		onSuccess: (isVerified) => {
			if (isVerified && !finishedRef.current && !cancelledRef.current) {
				finishedRef.current = true;
				stopPolling();
				setPhase(ORDER_STATUS_PHASE.SUCCESS);
			}
		},
		onError: (error) => {
			console.error('Payment verification failed:', error);
		},
	});

	const startVerification = () => {
		redirectStripeQuizFailure(paramsRef.current);

		if (!paramsRef.current.isStripeSuccess) {
			return;
		}

		finishedRef.current = false;
		cancelledRef.current = false;
		const startedAt = Date.now();

		const handleTimeout = () => {
			if (finishedRef.current || cancelledRef.current) {
				return;
			}

			finishedRef.current = true;
			stopPolling();
			setPhase(ORDER_STATUS_PHASE.FAILED);
			setTimeoutTriggered(true);
		};

		const runCheck = async () => {
			if (cancelledRef.current) {
				return;
			}

			const p = paramsRef.current;
			await verifyPayment({
				email: p.email,
				paymentIntent: p.paymentIntent,
			});

			if (
				!finishedRef.current &&
				Date.now() - startedAt >= ORDER_STATUS_TIMING.PAYMENT_VERIFY_TIMEOUT_MS
			) {
				handleTimeout();
			}
		};

		runCheck();
		intervalIdRef.current = window.setInterval(
			runCheck,
			ORDER_STATUS_TIMING.POLL_INTERVAL_MS,
		);
		timeoutIdRef.current = window.setTimeout(
			handleTimeout,
			ORDER_STATUS_TIMING.PAYMENT_VERIFY_TIMEOUT_MS,
		);
	};

	useEffect(() => {
		startVerification();

		return () => {
			stopPolling();
		};
	}, []);

	return {
		phase,
		params,
		timeoutTriggered,
	};
};

export default useOrderStatusPage;
